import React, { FC, useEffect, useState } from "react";
import { Button, Col, Flex, Form, message, Row } from "antd";
import { IUploadReferral } from "../../../interfaces/IReferral";
import { IDocumentFields } from "../../../interfaces/ICase";
import {
  PreAuthClaimField,
  PreAuthClaimResponse,
} from "../../../interfaces/IPreAuthClaim";
import { renderCommonField } from "../CommonFields";
import {
  CheckOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Empty } from "antd";
import { useUserFunctionalities } from "../../../utils/UserFunctionalities";
import type { ValidateErrorEntity } from "rc-field-form/lib/interface"; // Import ValidateError for errorInfo type
import Loader from "../../../utils/Spinners";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import ManualStatus from "../PreAuth/ManualStatusButton";
import { getVisitStageStates } from "../../../models/preAuthSlice";
import ClaimIDManuallyId from "../PreAuth/ClaimIDManuallyId";
import ManualPortalStatus from "../PreAuth/ManualPortalStatusButton";
import { errorMessageModal } from "../../../utils/modalMessage";

type FieldType = {
  additionalAmountPaid: boolean;
  amount: string;
};

const initialValues: FieldType = {
  additionalAmountPaid: false,
  amount: "",
};

type PreAuthClaimProps = {
  handleFileChange?: any;
  uploadFileModal: boolean;
  selectedFile: IUploadReferral;
  handleCancel: any;
  handleUploadDocument: any;
  viewFile: CallableFunction;
  handleDeleteList: CallableFunction;
  preAuthClaimData: PreAuthClaimResponse | null;
  caseClaimSubmissionId: number | null;
  editedFields: Record<string, Record<string, string>>;
  onFieldChange: (sectionKey: string, fieldId: string, value: string) => void;
  visitType: string | undefined;
  uploadedDocumentUrl: Array<IDocumentFields>;
  handleSaveChangesIhx: CallableFunction;
  handleSaveToEdgeProChangesIhx: CallableFunction;
  isLoading: boolean;
  isEditable: boolean;
  handleEditable: CallableFunction;
  handleSetDocumentUrl: CallableFunction;
  handleOpen: any;
  handleDownloadDocument: CallableFunction;
  isDownloadingDocument?: boolean;
  downloadingDocumentUrl?: string;
  isSubmitted: boolean;
  handleDeficiencyModal?: any;
  closeDeficiencyModal?: any;
  deficiencyModal: boolean;
  deficiencyQuestions?: any;
  previewDocumentUrl: string;
  setDocumentName: any;
  handleDeficiencyModalAfterSave: any;
  portalSubmission: string | null;
  nextStage: any;
  setMoveToNext: CallableFunction;
  handleApprove: any;
  isApproved: boolean;
  isApprovedInitiated: boolean;
};

const PreAuthClaimIhx: FC<PreAuthClaimProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [isAdditionalAmountPaid, setIsAdditionalAmountPaid] =
    useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const patientStore = useSelector((state: RootState) => state.patients);
  const { stepperData, currentStep, stageAllState } = useSelector(
    (state: RootState) => state.preAuth
  );

  const currentStageDetails = stepperData?.currentVisitStage;
  const currentActiveStage = stepperData?.visitStages[currentStep];

  const customStatus =
    currentActiveStage?.step && currentStageDetails?.step
      ? currentActiveStage.step === currentStageDetails.step
        ? currentStageDetails.stateId
        : currentActiveStage.step > currentStageDetails.step
        ? 8
        : 4
      : null;

  useEffect(() => {
    if (currentActiveStage?.visitStageId) {
      dispatch(
        getVisitStageStates({ visitStageId: currentActiveStage?.visitStageId })
      );
    }
  }, [currentActiveStage?.visitStageId]);

  const validateForm = async () => {
    try {
      await form.validateFields({ validateOnly: true });
      setIsFormValid(true);
    } catch (errorInfo) {
      setIsFormValid(false);
    }
  };

  useEffect(() => {
    // Set form values from editedFields
    if (props?.editedFields?.cghsClaimSubmissionResponse) {
      form.setFieldsValue(props?.editedFields?.cghsClaimSubmissionResponse);
    } else if (props?.editedFields?.ihxClaimSubmissionResponses) {
      form.setFieldsValue(props?.editedFields?.ihxClaimSubmissionResponses);
    }
  }, [props?.editedFields, form]);

  useEffect(() => {
    if (!props?.isSubmitted) {
      validateForm();
    }
    validateForm();
  }, [form, props?.editedFields]);

  const handleSubmit = async () => {
    if (props?.preAuthClaimData) {
      try {
        await form.validateFields();
        props?.handleSaveChangesIhx();
      } catch (errorInfo) {
        const validationError = errorInfo as ValidateErrorEntity<any>; // Correctly type `errorInfo`

        // Check if there are "required" errors
        const requiredErrors = validationError.errorFields?.some(
          (error: { errors: string[] }) =>
            error.errors?.some((msg: string) => msg.includes("is required"))
        );

        // Display a specific message based on the type of error
        if (requiredErrors) {
         errorMessageModal("Please fill in all required fields");
        } else {
          errorMessageModal(
            "Please correct the highlighted errors before submitting"
          );
        }
      }
    }
  };

  const handleSubmitForSavetoEdge = async () => {
    if (props?.preAuthClaimData) {
      try {
        await form.validateFields();
        props?.handleSaveToEdgeProChangesIhx();
      } catch (errorInfo) {
        const validationError = errorInfo as ValidateErrorEntity<any>; // Correctly type `errorInfo`

        // Check if there are "required" errors
        const requiredErrors = validationError.errorFields?.some(
          (error: { errors: string[] }) =>
            error.errors?.some((msg: string) => msg.includes("is required"))
        );

        // Display a specific message based on the type of error
        if (requiredErrors) {
          errorMessageModal("Please fill in all required fields");
        } else {
          errorMessageModal(
            "Please correct the highlighted errors before submitting"
          );
        }
      }
    }
  };

  useEffect(() => {
    for (const sectionKey in props.editedFields) {
      if (props.editedFields[sectionKey]?.additionalAmountPaid === "true") {
        setIsAdditionalAmountPaid(true);
      } else {
        setIsAdditionalAmountPaid(false);
      }
    }
  }, [props?.editedFields]);

  useEffect(() => {
    form.setFieldsValue(props?.preAuthClaimData);
  }, [props?.preAuthClaimData]);

  const renderTabContent = () => {
    if (!props?.preAuthClaimData) {
      return (
        <div style={{ width: "100%" }}>
          <Empty />
        </div>
      );
    }

    const responseKeys = [
      "ihxClaimSubmissionResponses",
      "cghsClaimSubmissionResponse",
      "echsClaimSubmissionResponse",
      "starHealthClaimSubmissionResponse",
      "vidalClaimSubmissionResponse",
    ];

    for (const key of responseKeys) {
      const data = props?.preAuthClaimData[key as keyof PreAuthClaimResponse];
      if (data && props?.visitType && key?.toLowerCase()?.includes("ihx")) {
        const sectionData = data;

        if (sectionData && Object.keys(sectionData).length > 0) {
          return (
            <Form
              form={form}
              onFinish={handleSubmit}
              labelAlign="left"
              labelWrap
              labelCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 8 },
                lg: { span: 6 },
              }}
              wrapperCol={{
                xs: { span: 24 },
                sm: { span: 12 },
                md: { span: 16 },
                lg: { span: 18 },
              }}
              style={{ width: "100%", marginLeft: "20px" }}
            >
              {Object?.entries(sectionData[0])?.map(([keys, fields], i) => {
                return (
                  <div key={i}>
                    {Array.isArray(fields) &&
                      fields?.map((field, index) => (
                        <div key={field.id}>
                          {renderCommonField({
                            field,
                            index,
                            isReadOnly:
                              !props?.isEditable && props?.caseClaimSubmissionId
                                ? true
                                : !field.updatable,
                            onFieldChange: (_, fieldId, value) =>
                              props?.onFieldChange(key, fieldId, value),
                            handleFileChange: props?.handleFileChange,
                            value:
                              props?.editedFields[key]?.[field.id] ??
                              field.data ??
                              "",
                            key: key,
                            form: form,
                            isAdditionalAmountPaid: isAdditionalAmountPaid,
                            caseSubmissionId: props?.caseClaimSubmissionId,
                            handleSetDocumentUrl: props?.handleSetDocumentUrl,
                            handleOpen: props?.handleOpen,
                            handleDownloadDocument:
                              props?.handleDownloadDocument,
                            isDownloadingDocument: props?.isDownloadingDocument,
                            downloadingDocumentUrl: props?.downloadingDocumentUrl,
                            handleDeleteList: props?.handleDeleteList,
                            uploadedDocumentUrl: props?.uploadedDocumentUrl,
                            handleDeficiencyModal: props?.handleDeficiencyModal,
                            closeDeficiencyModal: props?.closeDeficiencyModal,
                            deficiencyModal: props?.deficiencyModal,
                            deficiencyQuestions: props?.deficiencyQuestions,
                            previewDocumentUrl: props?.previewDocumentUrl,
                            setDocumentName: props?.setDocumentName,
                            handleDeficiencyModalAfterSave:
                              props?.handleDeficiencyModalAfterSave,
                          })}
                        </div>
                      ))}
                  </div>
                );
              })}
            </Form>
          );
        }
      }
    }

    return (
      <div style={{ width: "100%" }}>
        <Empty />
      </div>
    );
  };

  const handleEdit = () => {
    props?.handleEditable();
  };

  const { hasFunctionality, reloadFunctionalities } = useUserFunctionalities();
  useEffect(() => {
    // Optionally reload functionalities when component mounts
    reloadFunctionalities();
  }, [reloadFunctionalities]);

  return (
    <>
      {props?.preAuthClaimData ? (
        <>
          <div
            style={{
              border: "0.4px solid rgb(87, 87, 87)",
              gap: "15px",
              borderRadius: "16px",
              margin: "0px 0.5rem",
            }}
          >
            <div
              style={{
                minHeight: "64px",
                background: "rgb(243, 243, 243)",
                padding: "12px 35px", // Removed the semicolon and fixed the gap
                gap: "42px", // Added the gap as a separate property
                borderRadius: "16px 16px 0px 0px", // Fixed camelCase
                borderBottom: "1px solid rgb(166, 166, 166)",
                fontWeight: "600",
                fontSize: "16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "20px", alignContent: "center" }}>
                CLAIM SUBMISSION
              </span>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: "20px",
                  minWidth: "676px",
                  maxHeight: "36px",
                }}
              >
                <ClaimIDManuallyId />
                {currentActiveStage?.visitStageId &&
                  patientStore?.fetchBasicDetails?.visitInfoResponse?.id && (
                    <ManualStatus
                      stateData={
                        stageAllState?.[currentActiveStage?.visitStageId] ||
                        null
                      }
                      status={customStatus}
                      visitId={
                        patientStore?.fetchBasicDetails?.visitInfoResponse?.id
                      }
                      visitStageId={currentActiveStage?.visitStageId}
                      // isUpdateable={
                      //   customStatus == 4
                      //     ? false
                      //     : true
                      // }
                      isUpdateable={true}
                    />
                  )}
                {(patientStore?.fetchBasicDetails?.visitInfoResponse
                  ?.portalStatus ||
                  true) && (
                  <ManualPortalStatus
                    status={
                      patientStore?.fetchBasicDetails?.visitInfoResponse
                        ?.portalStatus || "-"
                    }
                    visitId={
                      patientStore?.fetchBasicDetails?.visitInfoResponse?.id
                    }
                    visitStageId={currentActiveStage?.visitStageId}
                    isUpdateable={
                      hasFunctionality("portalStatus") ? true : false
                    }
                  />
                )}
              </div>
            </div>
            <div style={{ padding: "20px 45px 20px 25px" }}>
              <div
                className="claim-submission"
                style={{
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                }}
              >
                {props?.preAuthClaimData &&
                props?.caseClaimSubmissionId &&
                (props?.portalSubmission === "save as draft" ||
                  props?.portalSubmission === null) ? (
                  props?.isEditable ? (
                    <Button
                      shape="circle"
                      icon={<CloseCircleOutlined />}
                      onClick={() => {
                        handleEdit();
                        handleSubmitForSavetoEdge();
                      }}
                    />
                  ) : (
                    <Button
                      shape="circle"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        handleEdit();
                      }}
                    />
                  )
                ) : (
                  ""
                )}
              </div>
              <Row justify="start">{renderTabContent()}</Row>
              {/* <Row justify="start">
                {!props?.caseClaimSubmissionId &&
                  props?.uploadedDocumentUrl?.length > 0 &&
                  props?.uploadedDocumentUrl?.map(
                    (item, index) =>
                      item?.documentName && (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginLeft: "50px",
                            marginTop: "10px",
                          }}
                        >
                          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                            <Flex
                              align="center"
                              justify="space-between"
                              gap="small"
                            >
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "black",
                                }}
                              >
                                Document
                              </div>

                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "#949494",
                                }}
                              >
                                {" "}
                                {item?.documentName?.toUpperCase()}
                              </div>
                              <div>
                                <Button
                                  shape="circle"
                                  icon={<EyeOutlined />}
                                  onClick={() => {
                                    props?.handleOpen && props?.handleOpen();
                                    props?.handleSetDocumentUrl &&
                                      "documentUrl" in item &&
                                      props?.handleSetDocumentUrl(
                                        item?.documentUrl
                                      );
                                  }}
                                  style={{
                                    color: "#273881",
                                    fontSize: "18px",
                                    marginRight: "10px",
                                  }}
                                />
                                <Button
                                  shape="circle"
                                  icon={<DownloadOutlined />}
                                  style={{
                                    color: "#273881",
                                    fontSize: "18px",
                                    marginRight: "10px",
                                  }}
                                  onClick={() => {
                                    props?.handleDownloadDocument &&
                                      props?.handleDownloadDocument(
                                        item?.documentName,
                                        item?.documentUrl
                                      );
                                  }}
                                />

                                <Button
                                  shape="circle"
                                  icon={<DeleteOutlined />}
                                  style={{
                                    color: "#273881",
                                    fontSize: "18px",
                                  }}
                                  onClick={() => {
                                    props?.handleDeleteList &&
                                      props?.handleDeleteList(item, index);
                                  }}
                                />
                                {item?.deficiencyCheckStatus ? (
                                  <Button
                                    onClick={() => {
                                      if (item && item?.documentName) {
                                        props?.setDocumentName(
                                          item?.documentName
                                        );
                                      }
                                      if (item?.documentType) {
                                        props?.handleDeficiencyModal(
                                          item?.documentUrl,
                                          parseInt(item?.documentType),
                                          document
                                        );
                                      }
                                    }}
                                    block
                                    type="primary"
                                    style={{ backgroundColor: "rgb(0, 109, 119)" }}
                                  >
                                    DEFICIENCY PASSED
                                  </Button>
                                ) : item?.deficiencyCheckStatus === false ? (
                                  <Button
                                    onClick={() => {
                                      if (item && item?.documentName) {
                                        props?.setDocumentName(
                                          item?.documentName
                                        );
                                      }
                                      if (item?.documentType) {
                                        props?.handleDeficiencyModal(
                                          item?.documentUrl,
                                          parseInt(item?.documentType),
                                          document
                                        );
                                      }
                                    }}
                                    block
                                    type="primary"
                                    danger
                                  >
                                    DEFICIENCY FAILED
                                  </Button>
                                ) : (
                                  (Array?.isArray(props?.deficiencyQuestions) ||
                                    typeof props?.deficiencyQuestions ===
                                      "object") && (
                                    <Button
                                      onClick={() => {
                                        if (item && item?.documentName) {
                                          props?.setDocumentName(
                                            item?.documentName
                                          );
                                        }
                                        if (item?.documentType) {
                                          props?.handleDeficiencyModal(
                                            item?.documentUrl,
                                            parseInt(item?.documentType),
                                            item
                                          );
                                        }
                                      }}
                                      block
                                      type="primary"
                                      htmlType="submit"
                                    >
                                      DEFICIENCY CHECK
                                    </Button>
                                  )
                                )}
                              </div>
                            </Flex>
                          </Col>
                        </div>
                      )
                  )}
              </Row> */}

              <Row justify="end" style={{ marginTop: "20px" }}>
                {/* Render the Save as Draft button if caseClaimSubmissionId is not present or isEditable is true */}
                {(!props?.caseClaimSubmissionId || props?.isEditable) &&
                  props?.preAuthClaimData?.ihxClaimSubmissionResponses &&
                  Object.keys(
                    props?.preAuthClaimData?.ihxClaimSubmissionResponses[0] ||
                      {}
                  ).length > 0 && (
                    <>
                      {hasFunctionality("claimCreation") && (
                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={props?.isLoading}
                            onClick={handleSubmitForSavetoEdge}
                            //   disabled={!isFormValid}
                            style={{ marginRight: "10px",
                              background: "#008B8B",
                              border: "1px solid #008B8B",
                              boxShadow: "0px 2px 8px 0px #00000040",
                              borderRadius: "10px",
                              padding: "12px 25px",
                              textTransform: "uppercase",
                              color: "white", }}
                          >
                            {!props?.caseClaimSubmissionId
                              ? "SAVE AS DRAFT"
                              : "UPDATE AS DRAFT"}
                          </Button>
                        </Form.Item>
                      )}
                    </>
                  )}

                <Form.Item style={{ marginBottom: "0px" }}>
                  {hasFunctionality("claimApprover") &&
                    (props.portalSubmission === "save as draft" ||
                      props.portalSubmission === null) && (
                      <Button
                        style={{
                          background: "#008B8B",
                          marginRight: "8px",
                          float: "right",
                        }}
                        disabled={props?.isApproved}
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={props?.handleApprove}
                        loading={props?.isApprovedInitiated}
                      >
                        Approve Claim
                      </Button>
                    )}
                </Form.Item>

                {/* Render the Submit on Portal button without the isEditable check */}
                {props?.preAuthClaimData?.ihxClaimSubmissionResponses &&
                  Object.keys(
                    props?.preAuthClaimData?.ihxClaimSubmissionResponses[0] ||
                      {}
                  ).length > 0 &&
                  hasFunctionality("claimSubmission") &&
                  (props.portalSubmission === "save as draft" ||
                    props.portalSubmission === null) && (
                    <Form.Item wrapperCol={{ span: 24 }}>
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSubmit}
                        loading={props?.isLoading}
                        style={{marginRight: "10px",
                          background: "#008B8B",
                          border: "1px solid #008B8B",
                          boxShadow: "0px 2px 8px 0px #00000040",
                          borderRadius: "10px",
                          padding: "12px 25px",
                          textTransform: "uppercase",
                          color: "white",}}
                      >
                        Submit on Portal
                      </Button>
                    </Form.Item>
                  )}
                {
                  !(
                    props?.portalSubmission === "save as draft" ||
                    props?.portalSubmission === null
                  ) && (
                    <Button
                      style={{
                        marginRight: "10px",
                        background: "#008B8B",
                        border: "1px solid #008B8B",
                        boxShadow: "0px 2px 8px 0px #00000040",
                        borderRadius: "10px",
                        padding: "12px 25px",
                        textTransform: "uppercase",
                        color: "white",
                      }}
                      onClick={() => {
                        props?.setMoveToNext(true);
                      }}
                    >
                      NEXT: {props?.nextStage?.visitStage}
                    </Button>
                  )}
              </Row>
            </div>
          </div>
        </>
      ) : (
        <>
          <Row justify={"center"}>
            <Loader />
          </Row>
        </>
      )}
    </>
  );
};

export default PreAuthClaimIhx;
